import {
  Button,
  Flex,
  Grid,
  Layout,
  Menu,
  MenuProps, Modal
} from "antd";
import {
  AppstoreOutlined,
  CalendarOutlined,
  CommentOutlined, EditOutlined,
  LogoutOutlined,
  QuestionCircleFilled, SettingOutlined,
  MenuOutlined,
  SnippetsOutlined
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import classes from "./sidebar.module.css";
import Logo from "../Logo";
import useSidebar from "./useSidebar";
import { appRoutes } from "../../constants/routes";
import Notifications from "../Notifications";
import useAssistance from "../../hooks/useAssistance";
import useCurrentUser from "../../hooks/useCurrentUser";
import useOnboarding from "../PatientDashboard/useOnboarding";
import OnboardingModal from "../PatientDashboard/onboardingModal";

const { Sider } = Layout;

type MenuItem = Required<MenuProps>["items"][number];

const Sidebar = () => {
  const [modal, contextHolder] = Modal.useModal();
  const { t } = useTranslation();
  const { toggleAssistModal } = useAssistance();
  const [collapsed, setCollapsed] = useState(false);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { isDoctor, isPatientApproved } = useCurrentUser();
  const {
    isModalOpen,
    toggleModal
  } = useOnboarding();

  const {
    logOut, currentKey, role, isLoading
  } = useSidebar();

  const shouldHideItem = useCallback((item: MenuItem) => {
    const isPatientRole = role === "patient";

    return isPatientRole && item?.key === appRoutes.availability;
  }, [role]);

  const canCollapse = !screens.lg && !collapsed;

  const items = [
    {
      key: appRoutes.root,
      icon: <AppstoreOutlined size={24}/>,
      label: <Link to={appRoutes.root}>{t("global.sidebar.dashboard")}</Link>,
    },
    {
      key: appRoutes.appointments,
      icon: <CalendarOutlined size={24} />,
      label: <Link to={appRoutes.appointments}>{t("global.sidebar.appointments")}</Link>,
    },
    {
      key: appRoutes.messages,
      icon: <CommentOutlined size={24} />,
      label: <Link to={appRoutes.messages}>{t("global.sidebar.messages")}</Link>,
    },
    {
      key: appRoutes.availability,
      icon: <EditOutlined size={24} />,
      label: <Link to={appRoutes.availability}>{t("global.sidebar.availability")}</Link>,
    },
    ...(!isDoctor ? [{
      key: "agreement",
      icon: <SnippetsOutlined />,
      onClick: () => (isPatientApproved ? modal.info({
        title: t("global.sidebar.agreementLabel"),
        content: (
          <>
            {t("global.sidebar.agreementText")}{" "}
            <a href="mailto:supporto@inbuonasalute.eu">supporto@inbuonasalute.eu</a>
          </>
        )
      }) : toggleModal()),
      label: "Consenso informato",
    }] : []),
    {
      key: appRoutes.settings,
      icon: <SettingOutlined />,
      label: <Link to={appRoutes.settings}>{t("global.sidebar.settings")}</Link>,
    }
  ];

  return (
    <>
      {!screens.lg ? (
        <>
          <header className={classes.header}>
            <Notifications outlined/>
            <Logo type="white" width={36} onlyIcon />
            <Button
              type="text"
              size="large"
              icon={<MenuOutlined />}
              onClick={() => setCollapsed(prevState => !prevState)}
              className={classes.menuButton}
            />
          </header>
          <span className={collapsed ? classes.overlayActive : classes.overlay} />
        </>
      ) : null}
      <Sider
        trigger={null}
        width={230}
        onClick={() => setCollapsed(false)}
        className={canCollapse ? classes.rootCollapsed : classes.root}
      >
        <Flex vertical className={classes.content}>
          {!screens.lg ? null
            : (
              <Flex justify="center" className={classes.logo}>
                <Logo width={151} type="white"/>
              </Flex>
            )}
          <Menu
            theme="dark"
            mode="inline"
            defaultSelectedKeys={["1"]}
            items={items.filter(item => !shouldHideItem(item))}
            className={classes.menu}
            selectedKeys={[currentKey]}
          />
          <Menu
            theme="dark"
            mode="inline"
            selectable={false}
            defaultSelectedKeys={["1", "2"]}
            items={[
              {
                key: "1",
                icon: <QuestionCircleFilled />,
                onClick: toggleAssistModal,
                label: t("global.sidebar.assistant"),
              },
              {
                key: "2",
                icon: <LogoutOutlined />,
                onClick: logOut,
                label: t("global.sidebar.logout"),
                disabled: isLoading
              }]}
            className={classes.actions}
          />
        </Flex>
      </Sider>
      <OnboardingModal isOpen={isModalOpen} handleToggle={toggleModal}/>
      {contextHolder}
    </>
  );
};

export default Sidebar;
